import { useAuth } from '@/contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireRole?: 'owner' | 'admin' | 'engineer' | 'viewer'
  requirePlatformAdmin?: boolean
}

const ROLE_HIERARCHY: Record<string, number> = {
  viewer: 0,
  engineer: 1,
  admin: 2,
  owner: 3,
}

export default function ProtectedRoute({ children, requireRole, requirePlatformAdmin }: ProtectedRouteProps) {
  // company.is_active já está disponível no AuthContext — sem queries extras por rota
  const { user, loading, blocked, company } = useAuth()
  const [adminChecked, setAdminChecked] = useState(!requirePlatformAdmin)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    if (!requirePlatformAdmin) {
      setAdminChecked(true)
      return
    }
    if (!user) {
      setAdminChecked(true)
      return
    }

    let cancelled = false
    setAdminChecked(false)

    async function check() {
      try {
        // Tenta via RPC SECURITY DEFINER (evita recursão de RLS na tabela platform_admins)
        const { data: rpcData, error: rpcError } = await supabase.rpc('is_platform_admin')
        if (!rpcError && typeof rpcData === 'boolean') {
          if (!cancelled) setIsAdmin(rpcData)
          return
        }
        if (rpcError) {
          console.warn('[ProtectedRoute] RPC is_platform_admin falhou, tentando SELECT direto:', rpcError)
        }
        // Fallback: SELECT direto
        const { data, error } = await supabase
          .from('platform_admins')
          .select('id')
          .eq('user_id', user!.id)
          .maybeSingle()
        if (error) {
          console.warn('[ProtectedRoute] platform_admins query error (tratando como não-admin):', error)
        }
        if (!cancelled) setIsAdmin(!!data)
      } catch (err) {
        console.error('ProtectedRoute: admin check failed', err)
        if (!cancelled) setIsAdmin(false)
      } finally {
        if (!cancelled) setAdminChecked(true)
      }
    }

    check()
    return () => { cancelled = true }
  }, [requirePlatformAdmin, user])

  if (loading || !adminChecked) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'hsl(var(--amber))', borderTopColor: 'transparent' }} />
      </div>
    )
  }

  // blocked = profile not found OR status !== 'aprovado' (session preserved, no signOut)
  if (!user && blocked) {
    return <Navigate to="/aguardando-aprovacao" replace />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Usa company.is_active já carregado no AuthContext (sem query extra por rota)
  const companyBlocked = company?.is_active === false

  // Plataforma admin não é bloqueado por empresa inativa
  if (companyBlocked && !(requirePlatformAdmin && isAdmin)) {
    return <Navigate to="/aguardando-aprovacao" replace />
  }

  if (requirePlatformAdmin && !isAdmin) {
    return <Navigate to="/" replace />
  }

  if (requireRole) {
    const userLevel = ROLE_HIERARCHY[user.company_role || 'viewer'] ?? 0
    const requiredLevel = ROLE_HIERARCHY[requireRole] ?? 0
    if (userLevel < requiredLevel) {
      return <Navigate to="/" replace />
    }
  }

  return <>{children}</>
}
